import { createAsyncThunk } from "@reduxjs/toolkit";

import { mapFSProjects } from "../Types/FinishingSchedule/FinishingSchedule";
import { mapFSAppliances } from "../Types/FinishingSchedule/Appliance";
import { mapFSBrands } from "../Types/FinishingSchedule/Brand";
import { mapFSColors } from "../Types/FinishingSchedule/Color";
import { mapFSDocuments } from "../Types/FinishingSchedule/Document";
import { mapFSItems } from "../Types/FinishingSchedule/Item";
import { mapFSLocations } from "../Types/FinishingSchedule/Location";
import { mapFSSizes } from "../Types/FinishingSchedule/Size";
import { mapFSTypes } from "../Types/FinishingSchedule/Type";
import { mapFSPaints } from "../Types/FinishingSchedule/Paint/Paint";
import { mapFSAccessoryNames } from "../Types/FinishingSchedule/Paint/AccessoryName";
import { mapFSAccessorySubNames } from "../Types/FinishingSchedule/Paint/AccessorySubName";
import { mapFSFloorFinishings } from "../Types/FinishingSchedule/FloorFinishing/FloorFinishing";
import { mapFSTiles } from "../Types/FinishingSchedule/Tile/Tile";
import { mapFSTileShops } from "../Types/FinishingSchedule/Tile/TileShop";
import { mapFSDoors } from "../Types/FinishingSchedule/Door/Door";
import { mapFSDoorSizes } from "../Types/FinishingSchedule/Door/DoorSize";

import { getFSProjectData } from "../../Api/FinishingScheduleService/FinishingScheduleService";
import { getFSApplianceList } from "../../Api/FinishingScheduleService/ApplianceService";
import { getFSBrandList } from "../../Api/FinishingScheduleService/BrandService";
import { getFSColorList } from "../../Api/FinishingScheduleService/ColorService";
import { getFSDocumentList } from "../../Api/FinishingScheduleService/DocumentService";
import { getFSItemList } from "../../Api/FinishingScheduleService/ItemService";
import { getFSLocationList } from "../../Api/FinishingScheduleService/LocationService";
import { getFSSizeList } from "../../Api/FinishingScheduleService/SizeService";
import { getFSTypeList } from "../../Api/FinishingScheduleService/TypeService";
import { getFSPaintList } from "../../Api/FinishingScheduleService/PaintService/PaintService";
import { getFSAccessoryList } from "../../Api/FinishingScheduleService/PaintService/AccessoryService";
import { getFSAccessorySubList } from "../../Api/FinishingScheduleService/PaintService/AccessorySubService";
import { getFSTileList } from "../../Api/FinishingScheduleService/TilesService/TilesService";
import { getFSTileShopList } from "../../Api/FinishingScheduleService/TilesService/TileShop";
import { getFSFloorFinishingList } from "../../Api/FinishingScheduleService/FloorFinishingService/FloorFinishingService";
import { getFSDoorList, getFSDoorSizeList } from "../../Api/FinishingScheduleService/DoorService/DoorService";
import { getFSBathroomList } from "../../Api/FinishingScheduleService/BathroomService/BathroomService";
import { getFSLightList } from "../../Api/FinishingScheduleService/LightService/LightService";


// fetch finishing schedule projects
export const fetchFSProjectList = createAsyncThunk(
    'fetchFSProjectList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSProjectData();
            if (response.status) {
                return mapFSProjects(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch finishing schedule projects data');
        }
    }
);

// fetch appliance
export const fetchFSApplianceList = createAsyncThunk(
    'fetchFSApplianceList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSApplianceList();
            if (response.status) {
                return mapFSAppliances(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch appliance data');
        }
    }
);

// fetch brand
interface FSBrandListArgs {
    category: 'Kitchen' | 'Paint';
    sub_category?:
    | 'Type'
    | 'Base Units'
    | 'Wall Units'
    | 'Worktop'
    | 'Door'
    | 'Handle'
    | 'Appliances'
    | 'Paint'
    | null;
}

export const fetchFSBrandList = createAsyncThunk(
    'fetchFSBrandList',
    async ({ category, sub_category }: FSBrandListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSBrandList(category, sub_category!);
            if (response.status) {
                return mapFSBrands(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch brand data');
        }
    }
);

// fetch color
interface FSColorListArgs {
    category: 'Kitchen' | 'Paint' | 'Tiles';
    sub_category?:
    | 'Type'
    | 'Base Units'
    | 'Wall Units'
    | 'Worktop'
    | 'Door'
    | 'Handle'
    | 'Appliances'
    | 'Paint'
    | null;
}

export const fetchFSColorList = createAsyncThunk(
    'fetchFSColorList',
    async ({ category, sub_category }: FSColorListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSColorList(category, sub_category!);
            if (response.status) {
                return mapFSColors(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch color data');
        }
    }
);

// fetch document
export const fetchFSDocumentList = createAsyncThunk(
    'fetchFSDocumentList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSDocumentList(fs_feature_id);
            if (response.status) {
                return mapFSDocuments(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch document data');
        }
    }
);

// fetch item
export const fetchFSItemList = createAsyncThunk(
    'fetchFSItemList',
    async (category: 'Light' | 'Bathroom', { rejectWithValue }) => {
        try {
            const response = await getFSItemList(category);
            if (response.status) {
                return mapFSItems(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch item data');
        }
    }
);

// fetch location
interface FSLocationListArgs {
    category: 'Light' | 'Paint' | 'Bathroom' | 'Tiles' | 'Floor Finishing' | 'Others';
    sub_category?: 'Light' | 'Paint' | 'Bathroom' | 'Tiles' | 'Floor Finishing' | 'Others' | null;
}

export const fetchFSLocationList = createAsyncThunk(
    'fetchFSLocationList',
    async ({ category, sub_category }: FSLocationListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSLocationList(category, sub_category!);
            if (response.status) {
                return mapFSLocations(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch location data');
        }
    }
);

// fetch size
export const fetchFSSizeList = createAsyncThunk(
    'fetchFSSizeList',
    async (category: 'Paint' | 'Floor Finishing', { rejectWithValue }) => {
        try {
            const response = await getFSSizeList(category);
            if (response.status) {
                return mapFSSizes(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch size data');
        }
    }
);

// fetch type
export const fetchFSTypeList = createAsyncThunk(
    'fetchFSTypeList',
    async (category: 'Tiles' | 'Light' | 'Floor Finishing', { rejectWithValue }) => {
        try {
            const response = await getFSTypeList(category);
            if (response.status) {
                return mapFSTypes(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch type data');
        }
    }
);

// fetch paint
export const fetchFSPaintList = createAsyncThunk(
    'fetchFSPaintList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSPaintList(fs_feature_id);
            if (response.status) {
                return mapFSPaints(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch paint data');
        }
    }
);

// fetch accessory name
export const fetchFSAccessoryNameList = createAsyncThunk(
    'fetchFSAccessoryNameList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSAccessoryList();
            if (response.status) {
                return mapFSAccessoryNames(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch accessory name data');
        }
    }
);

// fetch accessory sub name
export const fetchFSAccessorySubNameList = createAsyncThunk(
    'fetchFSAccessorySubNameList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSAccessorySubList();
            if (response.status) {
                return mapFSAccessorySubNames(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch accessory sub name data');
        }
    }
);

// fetch floor finishing
export const fetchFSFloorFinishingList = createAsyncThunk(
    'fetchFSFloorFinishingList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSFloorFinishingList(fs_feature_id);
            if (response.status) {
                return mapFSFloorFinishings(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch floor finishing data');
        }
    }
);

// fetch tiles
export const fetchFSTileList = createAsyncThunk(
    'fetchFSTileList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSTileList(fs_feature_id);
            if (response.status) {
                return mapFSTiles(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch tiles data');
        }
    }
);

// fetch tile shop
export const fetchFSTileShopList = createAsyncThunk(
    'fetchFSTileShopList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSTileShopList();
            if (response.status) {
                return mapFSTileShops(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch tile shop data');
        }
    }
);

// fetch door
export const fetchFSDoorList = createAsyncThunk(
    'fetchFSDoorList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSDoorList(fs_feature_id);
            if (response.status) {
                return mapFSDoors(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch door data');
        }
    }
);

// fetch door size
export const fetchFSDoorSizeList = createAsyncThunk(
    'fetchFSDoorSizeList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSDoorSizeList();
            if (response.status) {
                return mapFSDoorSizes(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch door size data');
        }
    }
);
